﻿import {Core} from "../../Base/Core";
import {GameManager} from "../GameManager";
import {Time} from "../../Utils/Unity/Time";
import {UIBase} from "../../UI/Tools/Base/UIBase";
import {BubbleView} from "./BubbleView";
import {CreatureView} from "./CreatureView";
import {gsap} from "gsap";
import {UISprite} from "../../UI/Tools/Base/UISprite";

export class EpochsView extends UIBase {
    sortingTimer = 0;
    sortingPeriod = 0.25;

    Awake() {
        super.Awake();
        this.background = null;
        this.epochsController = null; // EpochsController
        this.creatureViews = []; // List<CreatureView>
        this.bubbleViews = []; // List<BubbleView>
        this.allEntities = []; // List<EntityView>
        this.creaturesInBox = []; // List<int>
        this.pointerEntities = new Map();
        this.selectedEpochId = 0;
        this.backgroundAlpha = 0;
    }

    Init(epochsController) {
        this.epochsController = epochsController;
        this.creatureViews = []; // new List<CreatureView>();
        this.bubbleViews = []; // new List<BubbleView>();
        this.allEntities = []; // new List<EntityView>();
        this.pointerEntities = new Map();
        this.background = this.addChild(new UISprite());
        this.entitiesContainer = this.addChild(new PIXI.Container);
        this.vfxContainer = this.addChild(new PIXI.Container);
        this.SelectEpoch(Core.Player.PlanetsSave.CurrentPlanet.StartEpoch, false, 0);
        this.InitInteraction();
    }

    _getTopPointEntity(p) {
        let list = this._getPointEntities(p);
        return list.length > 0 ? list[0] : null;
    }

    _getPointEntities(p) {
        let list = [];
        // Loop through children to preserve z-index ordering (reverse order)
        for (let e of this.entitiesContainer.children) {
            if (this.allEntities.includes(e) && e.canInteract(p)) {
                list.unshift(e);
            }
        }
        return list;
    }

    _releasePointer(id) {
        for (let e of this.allEntities) {
            if (e.pointerId === id) {
                e.pointerId = null;
            }
        }
        this.pointerEntities.delete(id);
    }

    _releasePointers() {
        this.pointerEntities.clear();
        for (let e of this.allEntities) {
            e.pointerId = null;
        }
    }

    _pinPointer(id, entity) {
        this.pointerEntities.set(id, entity);
        entity.pointerId = id;
    }

    _handlePointerDown(e) {
        let {global, identifier} = e.data;
        this._releasePointer(identifier);
        let entities = this._getPointEntities(global);
        while (entities.length > 0) {
            let entity = entities.shift();
            if (entity.canInteract()) {
                this._pinPointer(identifier, entity);
                entity.mouseDown(global);
                return;
            }
        }

        // No entities found. Handle epoch change
        this._epochChangePointer = new PIXI.Point(global.x, global.y);
    }

    _handlePointerMove(e) {
        let {global, identifier} = e.data;
        let entity = this.pointerEntities.get(identifier);
        if (entity) {
            entity.mouseMove(global);
            return;
        }

        // Handle epoch change
        if (this._epochChangePointer) {
            let dx = this._epochChangePointer.x - global.x;
            if (Math.abs(dx) >= (Core.app.viewport.width * 0.5) / Core.app.renderer.resolution) {
                this._epochChangePointer = null;
                return dx > 0
                    ? this.SelectNextEpoch()
                    : this.SelectPrevEpoch()
            }
        }
    }

    _handlePointerUp(e) {
        let {global, identifier} = e.data;
        this._epochChangePointer = null;
        let entity = this.pointerEntities.get(identifier);
        this._releasePointer(identifier);
        if (entity) {
            entity.mouseUp(global);
        }
    }

    InitInteraction() {
        this.interactive = true;
        this._handlePointerDown = this._handlePointerDown.bind(this);
        this._handlePointerMove = this._handlePointerMove.bind(this);
        this._handlePointerUp = this._handlePointerUp.bind(this);
        this.on("pointerdown", this._handlePointerDown);
        this.on("pointermove", this._handlePointerMove);
        this.on("pointerup", this._handlePointerUp);
        this.on("pointerupoutside", this._handlePointerUp);

        GameManager.TapChallengeMode.on("Finished", () => {
            this._releasePointers();
        });
    }

    fitLayout(viewport) {
        let {width, height} = viewport;
        this.background.position.set(width * 0.5, height * 0.5);
        this.background.scale.set(Math.max(
            width / this.background.texture.width,
            height / this.background.texture.height
        ));
    }

    ClearBubbles() {
        for (let i = this.bubbleViews.length - 1; i >= 0; i--) {
            this.bubbleViews[i].RemoveView();
        }
    }

    SpawnBubble() {
        let bubbleView = this.entitiesContainer.addChild(new BubbleView());
        bubbleView.SpawnTo(GameManager.GetRandomPointInCameraView());
        this.bubbleViews.push(bubbleView);
        this.allEntities.push(bubbleView);
        this.sortingTimer = 0;
        this.updateCreaturesVisibility();
    }

    GetTopActiveCreature() {
        let topCreatureId = 0;
        let topView = null;//default(CreatureView);

        for (let c of this.creatureViews) {
            if (c.creatureController && c.creatureController.epoch.epochId == this.selectedEpochId) {
                if (topCreatureId < c.creatureController.creatureId) {
                    topCreatureId = c.creatureController.creatureId;
                    topView = c;
                }
            }
        }
        return topView;
    }

    SpawnCreature(creatureController, position, inBox) {
        let creatureView = this.entitiesContainer.addChild(new CreatureView());
        creatureView.Place(position);
        creatureView.Init(creatureController, inBox);
        this.creatureViews.push(creatureView);
        this.allEntities.push(creatureView);
        this.sortingTimer = 0;
        this.updateCreaturesVisibility();
        return creatureView;
    }

    RemoveCreatureView(creatureView) {
        let idx;
        idx = this.creatureViews.indexOf(creatureView);
        if (idx >= 0) this.creatureViews.splice(idx, 1);
        idx = this.allEntities.indexOf(creatureView);
        if (idx >= 0) this.allEntities.splice(idx, 1);
        if (creatureView.parent) {
            creatureView.parent.removeChild(creatureView);
        }
    }

    RemoveBubbleView(bubbleView) {
        let idx;
        idx = this.bubbleViews.indexOf(bubbleView);
        if (idx >= 0) this.bubbleViews.splice(idx, 1);
        idx = this.allEntities.indexOf(bubbleView);
        if (idx >= 0) this.allEntities.splice(idx, 1);
        if (bubbleView.parent) {
            bubbleView.parent.removeChild(bubbleView);
        }
    }

    GetCreatureForMerge(withCreature, ignorePosition = false) {
        for (let c of this.creatureViews) {
            if (c.canMerge(withCreature, !ignorePosition)) return c;
        }
        return null;
    }

    SelectEpoch(epochid, withAnimation, animationDirection) {
        this.selectedEpochId = epochid;
        this.updateCreaturesVisibility();
        this.updateEpochBackground(withAnimation, animationDirection);
        GameManager.callOnEpochSelected(epochid);
    }

    SelectNextEpoch() {
        for (let p of Core.Player.PlanetsSave.CurrentPlanet.epochs) {
            if (p > this.selectedEpochId) {
                this.SelectEpoch(p, true, 1);
                break;
            }
        }
    }

    SelectPrevEpoch() {
        for (let i = Core.Player.PlanetsSave.CurrentPlanet.epochs.length - 1; i > 0; i--) {
            let p = Core.Player.PlanetsSave.CurrentPlanet.epochs[i];
            if (p == this.selectedEpochId) {
                this.SelectEpoch(Core.Player.PlanetsSave.CurrentPlanet.epochs[i - 1], true, -1);
                break;
            }
        }
    }

    // ??? - добавить переменных чтобы вынести контроль в GameManager
    updateEpochBackground(withAnimation, direction) {

        if (withAnimation) {
            let prevBackground = this.addChildAt(new UISprite(this.background.texture), 0);
            prevBackground.scale.copyFrom(this.background.scale);
            prevBackground.position.copyFrom(this.background.position);

            let offset = this.background.getBounds().width * 0.5 * -direction;

            this.background.setImage(`epochs/${this.selectedEpochId}`);
            this.background.alpha = 0;
            this.background.pivot.x = offset;

            let entities = this.allEntities.filter(e => e.Active);
            for (let entity of entities) {
                entity.alpha = 0;
                entity.pivot.x = offset;
            }

            let duration = 0.3;
            let animation = gsap.timeline()
                .to(this.background, {alpha: 1, duration, ease: "power3.in"}, 0)
                .to(this.background.pivot, {x: 0, duration, ease: "power3.in"}, 0)
                .eventCallback("onComplete", () => {
                    this.removeChild(prevBackground);
                })
            ;
            for (let entity of entities) {
                animation
                    .to(entity, {alpha: 1, duration, ease: "power3.in"}, 0)
                    .to(entity.pivot, {x: 0, duration, ease: "back.out"}, 0)
                ;
            }
        } else {
            this.background.setImage(`epochs/${this.selectedEpochId}`);
        }
    }

    updateCreaturesVisibility() {
        let finalForm = null; // CreatureView
        let finalFormLevel = 0;

        this.creaturesInBox.splice(0);

        if (Core.Player.PlanetsSave.CurrentPlanet.creaturesInBox) {
            this.creaturesInBox.push(...Core.Player.PlanetsSave.CurrentPlanet.creaturesInBox);
        }

        for (let c of this.creatureViews) {
            if (c.creatureController.creatureData.IsFinalForm) {
                if (!finalForm) {
                    finalForm = c;
                    finalFormLevel++;
                } else {
                    c.SetActive(false);
                    finalFormLevel++;
                    continue;
                }
            }

            c.SetActive(c.creatureController.epoch.epochId == this.selectedEpochId);

            if (!c.creatureController.creatureData.IsFinalForm) {
                if (this.creaturesInBox.includes(c.creatureController.creatureData.id)) {
                    c.SetBox(true);
                    this.creaturesInBox.splice(this.creaturesInBox.indexOf(c.creatureController.creatureData.id), 1);
                }
            }
        }

        if (finalForm) {
            finalForm.SetLevel(finalFormLevel);
        }

        for (let b of this.bubbleViews) {
            b.SetActive(this.selectedEpochId == Core.Player.PlanetsSave.CurrentPlanet.StartEpoch);
        }
    }

    Update() {
        for (let entity of this.allEntities) {
            entity.Update();
        }

        this.sortingTimer -= Time.deltaTime;
        if (this.sortingTimer <= 0) {
            this.sortingTimer = this.sortingPeriod;
            this.entitiesContainer.children.sort((a, b) => a.sprite.getBounds().bottom - b.sprite.getBounds().bottom);
        }
    }

}
